import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Divider,
  Alert,
  Fade,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';

import {
  CloudUpload as UploadIcon,
  PlayArrow as AnalyzeIcon,
  CheckCircle as CheckIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import axios from 'axios';

const API_URL = '/api';

const UploadArea = styled(Paper)(({ theme, isDragging }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: isDragging ? theme.palette.action.hover : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  borderRadius: theme.spacing(2),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`acoustic-tabpanel-${index}`}
      aria-labelledby={`acoustic-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AcousticAnalysis = () => {
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState(0);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [patientId, setPatientId] = useState('');
  const [analysisHistory, setAnalysisHistory] = useState([]);

  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {};
  }, []);

  const addLog = useCallback((message) => {
    setLogs(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      message,
    }]);
  }, []);

  const handleFileUpload = useCallback((file) => {
    if (!file) return;

    const validTypes = ['audio/wav', 'audio/mpeg', 'audio/flac', 'audio/ogg'];
    const validExt = ['.wav', '.mp3', '.flac', '.ogg'];
    const hasValidType = validTypes.includes(file.type);
    const hasValidExt = validExt.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!hasValidType && !hasValidExt) {
      setError('Please upload a valid audio file');
      return;
    }

    // REDUCED LIMIT TO 10MB to avoid 413 errors
    if (file.size > 10 * 1024 * 1024) {
      setError('File size exceeds 10MB limit. Please use a smaller file.');
      return;
    }

    setFile(file);
    setError(null);
    setResult(null);
    setLogs([]);

    if (!patientId) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      setPatientId(nameWithoutExt);
    }

    addLog(`📁 Uploaded: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
  }, [patientId, addLog]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileUpload(droppedFile);
    }
  }, [handleFileUpload]);

  const handleFileInput = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      handleFileUpload(selectedFile);
    }
  }, [handleFileUpload]);

  const removeFile = useCallback((e) => {
    if (e) e.stopPropagation();
    setFile(null);
    setResult(null);
    setError(null);
    setProgress(0);
    addLog('🗑️ Audio removed');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [addLog]);

  // NEW: Compress audio function
  const compressAudio = useCallback(async (file) => {
    // If file is small enough, return as is
    if (file.size < 2 * 1024 * 1024) {
      return file;
    }

    try {
      addLog('🔄 Compressing audio file...');
      
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Convert to mono and downsample to 16kHz
      const targetSampleRate = 16000;
      const offlineContext = new OfflineAudioContext(
        1, // mono
        Math.floor(audioBuffer.length * targetSampleRate / audioBuffer.sampleRate),
        targetSampleRate
      );
      
      const source = offlineContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(offlineContext.destination);
      source.start();
      
      const renderedBuffer = await offlineContext.startRendering();
      
      // Convert to WAV blob
      const wavBlob = await audioBufferToWav(renderedBuffer);
      const compressedFile = new File(
        [wavBlob], 
        file.name.replace(/\.[^/.]+$/, '') + '_compressed.wav',
        { type: 'audio/wav' }
      );
      
      addLog(`✅ Compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
      return compressedFile;
    } catch (err) {
      addLog(`⚠️ Compression failed, using original file: ${err.message}`);
      return file;
    }
  }, [addLog]);

  // Helper: Convert AudioBuffer to WAV
  const audioBufferToWav = (buffer) => {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1;
    const bitDepth = 16;
    
    const samples = buffer.getChannelData(0);
    const dataLength = samples.length * (bitDepth / 8);
    const bufferLength = 44 + dataLength;
    
    const arrayBuffer = new ArrayBuffer(bufferLength);
    const view = new DataView(arrayBuffer);
    
    const writeString = (view, offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * (bitDepth / 8), true);
    view.setUint16(32, numChannels * (bitDepth / 8), true);
    view.setUint16(34, bitDepth, true);
    writeString(view, 36, 'data');
    view.setUint32(40, dataLength, true);
    
    const offset = 44;
    for (let i = 0; i < samples.length; i++) {
      const sample = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(offset + i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
    }
    
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

  const handleAnalyze = useCallback(async () => {
    if (!file) {
      setError('Please upload an audio file first');
      return;
    }

    setIsLoading(true);
    setProgress(0);
    setError(null);
    setResult(null);
    addLog('🚀 Starting acoustic analysis...');

    try {
      setProgress(10);
      
      // Compress audio if needed
      const fileToUpload = await compressAudio(file);
      
      setProgress(20);
      addLog('📤 Uploading audio file...');

      const formData = new FormData();
      formData.append('patient_id', patientId || 'unknown');
      formData.append('file', fileToUpload);

      setProgress(40);
      addLog('🧠 Analyzing audio with AI model...');

      const response = await axios.post(
        `${API_URL}/v1/predict/acoustic`,
        formData,
        {
          headers: {
            'X-CTF-API-Key': 'test-key-123',
            // Don't set Content-Type - axios will set it with boundary
          },
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
          timeout: 300000, // 5 minutes
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total > 0) {
              const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setProgress(Math.min(30 + pct * 0.7, 95));
            }
          },
        }
      );

      console.log('📥 Acoustic API Response:', response.data);

      setProgress(100);
      const data = response.data;
      setResult(data);

      const historyEntry = {
        id: Date.now(),
        patientId: patientId || 'unknown',
        filename: file.name,
        timestamp: new Date().toISOString(),
        result: data,
      };

      setAnalysisHistory((prev) => [historyEntry, ...prev].slice(0, 10));
      addLog('✅ Analysis complete!');
      addLog(`🎯 Prediction: ${data.predicted_label || 'Unknown'}`);
      addLog(`📊 TB Probability: ${(Number(data.tb_probability || 0) * 100).toFixed(1)}%`);
      
    } catch (err) {
      console.error('Analysis error:', err);
      
      // Better error handling
      let errorMessage = err.message || 'Analysis failed';
      
      if (err.response?.status === 413) {
        errorMessage = 'Audio file is too large for the server. Please use a shorter recording or compress it.';
        addLog('❌ 413 Error: File too large - server rejected the upload');
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. The server is taking too long to respond.';
        addLog('⏰ Request timeout - server took too long');
      }
      
      setError(errorMessage);
      addLog(`❌ Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  }, [file, patientId, addLog, compressAudio]);

  const resetAnalysis = useCallback(() => {
    setFile(null);
    setResult(null);
    setError(null);
    setProgress(0);
    setLogs([]);
    setPatientId('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    addLog('🔄 Analysis reset');
  }, [addLog]);

  const handleDownload = useCallback(() => {
    if (!result) return;

    const dataStr = JSON.stringify(result, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `acoustic_result_${patientId || 'unknown'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [result, patientId]);

  const renderUploadArea = useCallback(() => (
    <UploadArea
      isDragging={isDragging}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInput}
        accept=".wav,.mp3,.flac,.ogg,audio/*"
        style={{ display: 'none' }}
      />
      {file ? (
        <Box>
          <CheckIcon color="success" sx={{ fontSize: 60 }} />
          <Typography variant="h6">{file.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {(file.size / 1024 / 1024).toFixed(2)} MB
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {file.type || 'audio file'}
          </Typography>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={removeFile}
            sx={{ mt: 1 }}
          >
            Remove
          </Button>
        </Box>
      ) : (
        <Box>
          <UploadIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
          <Typography variant="h6" color="text.secondary">
            Drag & drop an audio file here
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to browse (WAV, MP3, FLAC, OGG) - Max 10MB
          </Typography>
        </Box>
      )}
    </UploadArea>
  ), [file, isDragging, handleDragOver, handleDragLeave, handleDrop, handleFileInput, removeFile]);

  const renderResults = useCallback(() => {
    if (!result) return null;

    const tbProb = Number(result.tb_probability || 0) * 100;
    const confidence = Number(result.confidence || 0) * 100;

    return (
      <Fade in={!!result} timeout={500}>
        <Box>
          <Card sx={{ mb: 3, bgcolor: result.predicted_label === 'TB' ? 'error.light' : 'success.light' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                🎯 Prediction: {result.predicted_label || 'Unknown'}
              </Typography>
              <Typography variant="h6">
                TB Probability: {tbProb.toFixed(1)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={tbProb}
                sx={{ height: 12, borderRadius: 6, mt: 1 }}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Confidence: {confidence.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Model: {result.encoder_version || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available: {String(result.available ?? 'N/A')}
                </Typography>
                {result.checkpoint_hash && (
                  <Typography variant="body2" color="text.secondary">
                    Checkpoint: {result.checkpoint_hash.slice(0, 16)}...
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          <Alert severity="info" sx={{ mt: 2 }}>
            {result.predicted_label === 'TB'
              ? `Model detected TB with ${tbProb.toFixed(1)}% probability.`
              : `Model detected normal with ${(100 - tbProb).toFixed(1)}% confidence.`}
          </Alert>
        </Box>
      </Fade>
    );
  }, [result]);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              🎧 Acoustic Analysis Lab
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Upload respiratory audio for AI-powered diagnostic analysis
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/platform')}>
            Back to Platform
          </Button>
        </Box>
        <Divider sx={{ mt: 2 }} />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📤 Upload Audio
              </Typography>

              {renderUploadArea()}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mt: 2 }}>
                <input
                  type="text"
                  placeholder="Patient ID"
                  value={patientId}
                  onChange={(e) => setPatientId(e.target.value)}
                  style={{
                    padding: '8px',
                    width: '100%',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    marginBottom: '10px',
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleAnalyze}
                  disabled={!file || isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <AnalyzeIcon />}
                >
                  {isLoading ? 'Analyzing...' : 'Run Analysis'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetAnalysis}
                  disabled={isLoading}
                  startIcon={<RefreshIcon />}
                >
                  Reset
                </Button>
              </Box>

              {isLoading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                    {progress}% Complete
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)}>
                  <Tab label="📊 Results" />
                  <Tab label="📝 Logs" />
                </Tabs>
              </Box>

              <TabPanel value={selectedTab} index={0}>
                {renderResults()}
              </TabPanel>

              <TabPanel value={selectedTab} index={1}>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {logs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                      No logs yet. Run analysis to see the process.
                    </Typography>
                  ) : (
                    logs.map((log, idx) => (
                      <Box key={idx} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                          [{log.timestamp}]
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {log.message}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </TabPanel>
            </CardContent>
          </Card>

          {analysisHistory.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Analysis History (Last 10)
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {analysisHistory.map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {entry.filename}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Patient: {entry.patientId} | Prediction: {entry.result.predicted_label || 'Unknown'} | Confidence: {(Number(entry.result.confidence || 0) * 100).toFixed(2)}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default AcousticAnalysis;
